import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import {
  RULE_EXPECTATIONS,
  attempt,
  accessDetail,
  fmtExpected,
  probe
} from '../ruleExpectations.js'
import { buildEvaluationRecord, inferHiddenOpenEval } from '@/utils/studentEvaluation.js'
import { canManageMeetings } from '@/utils/meetingPermissions'

// DESTRUCTIVE (dev project only): smoke §17 — the eval-CM mirror end to end,
// against REAL Cloud Functions, INCLUDING the consent gate (split-grant
// FERPA design, 2026-10-20). Creates a tagged 504 student owned by a fake
// other CM, opens an IEP eval assigned to the CURRENT user with a referral
// date only, and asserts the referral window first:
//
//   - staffIds gains the eval CM (unlabeled access),
//   - app.evalCaseManagerId / app.evalMeetingDate stay ABSENT (the yellow
//     Initial tile must not announce an unconsented referral to teachers),
//   - evalAccess/{studentId} names the CM (admin/psych/owner readable).
//
// Then signs the assessment plan (dates.consent) and waits for the public
// mirror to land. Asserts the meeting-write clause with a real meetings
// create, deletes the eval, and confirms mirror + staffIds + grant clear.
//
// Delete of an open eval does not clear app.evalCaseManagerId in the same
// write — the CF does that, same as close. inferHiddenOpenEval already treats
// a leftover own-uid mirror as lag, not "someone else has an open eval."
//
// Needs functions deployed. Requires a role that can create students AND
// evaluations (chairs / psych / admin — not a plain CM; that create grant
// was removed 2026-08-30). Other roles are covered by §16 / permission probes.

const OTHER_CM = 'smoke-other-cm'
const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 75000

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Poll the student doc until `check(state)` returns a truthy detail string.
 * A permission-denied read is passed to `check` as { denied: true } — before
 * the mirror lands, a plain CM cannot read the student at all, and after the
 * eval is deleted that denial coming BACK is proof the uid left staffIds.
 */
async function pollStudent(ref, check) {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let last = 'no read yet'
  while (Date.now() < deadline) {
    let state
    try {
      const snap = await getDoc(ref)
      state = { denied: false, exists: snap.exists(), data: snap.exists() ? snap.data() : null }
    } catch (err) {
      if (err?.code !== 'permission-denied') throw err
      state = { denied: true, exists: null, data: null }
    }
    const result = check(state)
    if (result) return result
    last = state.denied
      ? 'read denied (not in staffIds yet)'
      : `evalCaseManagerId=${JSON.stringify(state.data?.app?.evalCaseManagerId ?? null)}`
    await sleep(POLL_INTERVAL_MS)
  }
  throw new Error(
    `Timed out after ${POLL_TIMEOUT_MS / 1000}s waiting for Cloud Function (last: ${last}). ` +
      'Are functions deployed to this project?'
  )
}

export default {
  id: 'evalCaseManagerMirror',
  label: 'Eval CM mirror + consent gate (real student + Cloud Function)',
  description:
    'Creates a tagged 504 student + IEP eval assigned to you (referral only). Asserts the referral window: staffIds access + evalAccess grant, NO public mirror. Signs AP consent, waits for the mirror, asserts the meeting-write clause, then verifies everything clears. Needs deployed functions; takes ~60–120s.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const uid = ctx.user.uid

    if (!ctx.schoolId) {
      step.block('setup', 'Select a school before running the eval mirror smoke.')
      return
    }
    if (
      !RULE_EXPECTATIONS.studentCreate(role) ||
      !RULE_EXPECTATIONS.studentEvaluationCreate(role)
    ) {
      step.block(
        'setup',
        `Role ${role} cannot create students/evaluations — denial paths are covered by §16 and the permission probes.`
      )
      return
    }

    const studentRef = doc(collection(db, 'students'))
    const evalRef = doc(collection(db, 'studentEvaluations'))
    const meetingRef = doc(collection(db, 'meetings'))

    // 504 student owned by someone else — the Jepson shape. The current user
    // has NO relationship to them until the mirror lands.
    const studentDoc = {
      app: {
        studentData: {
          firstName: 'SMOKE',
          lastName: `EvalMirror ${ctx.runId.slice(-8)}`,
          plan: '504',
          grade: '9',
          ssid: `SMOKE-EVALCM-${ctx.runId}`,
          caseManagerId: OTHER_CM
        },
        accommodations: { instruction: '', assessment: '' },
        schedule: { classServices: [] },
        staffIds: []
      },
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      schoolId: ctx.schoolId
    }

    // The client mirror decides the meeting expectation: CM-type roles pass
    // via the eval clause; psych and plain admins stay denied (decision 1).
    const meetingStudentShape = {
      app: {
        studentData: { caseManagerId: OTHER_CM, plan: '504' },
        evalCaseManagerId: uid
      }
    }
    const expectMeeting = canManageMeetings({ uid, role }, meetingStudentShape)

    let evalCreated = false
    try {
      await step('create 504 student owned by another CM', async () => {
        await setDoc(studentRef, studentDoc)
        return `students/${studentRef.id} (plan 504, caseManagerId ${OTHER_CM})`
      })

      await step('create open IEP eval assigned to you', async () => {
        const record = buildEvaluationRecord({
          studentId: studentRef.id,
          schoolId: ctx.schoolId,
          evalType: 'IEP',
          caseManagerId: uid,
          createdBy: uid,
          dates: { referral: '2026-08-01' }
        })
        await setDoc(evalRef, {
          ...record,
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        evalCreated = true
        return `studentEvaluations/${evalRef.id}`
      })

      await step('referral window: staffIds access lands, public mirror stays ABSENT', async () => {
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied || !data) return null
          const staffIds = data.app?.staffIds || []
          if (!staffIds.includes(uid)) return null
          const mirror = data.app?.evalCaseManagerId || ''
          if (mirror) {
            throw new Error(
              `FERPA: evalCaseManagerId mirror (${mirror}) written BEFORE AP consent — ` +
                'the referral is announced on the teacher-readable student doc'
            )
          }
          if (data.app?.evalMeetingDate) {
            throw new Error('FERPA: evalMeetingDate mirror written before AP consent')
          }
          if (data.app?.studentData?.plan !== '504') {
            throw new Error(`SECURITY: student plan changed to ${data.app?.studentData?.plan}`)
          }
          if (data.app?.studentData?.caseManagerId !== OTHER_CM) {
            throw new Error('SECURITY: student caseManagerId was reassigned')
          }
          return 'uid in staffIds; evalCaseManagerId / evalMeetingDate absent (tile hidden)'
        })
      })

      await step('referral window: evalAccess grant names you', async () => {
        const snap = await getDoc(doc(db, 'evalAccess', studentRef.id))
        if (!snap.exists()) {
          throw new Error('evalAccess grant missing — pre-consent CM access would be broken')
        }
        const cm = snap.data()?.caseManagerId || ''
        if (cm !== uid) throw new Error(`evalAccess names ${cm}, expected ${uid}`)
        return `evalAccess/${studentRef.id} caseManagerId=${uid}`
      })

      await step('sign AP consent → CF writes the public mirror', async () => {
        await updateDoc(evalRef, {
          dates: {
            referral: '2026-08-01',
            planSent: '',
            consent: '2026-08-15',
            due: '',
            meeting: '',
            closed: ''
          },
          updatedAt: serverTimestamp()
        })
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied || !data) return null
          const mirror = data.app?.evalCaseManagerId || ''
          const staffIds = data.app?.staffIds || []
          if (mirror !== uid || !staffIds.includes(uid)) return null
          if (data.app?.studentData?.plan !== '504') {
            throw new Error(`SECURITY: student plan changed to ${data.app?.studentData?.plan}`)
          }
          if (data.app?.studentData?.caseManagerId !== OTHER_CM) {
            throw new Error('SECURITY: student caseManagerId was reassigned')
          }
          return `mirror + staffIds set after consent; plan still 504, caseManagerId still ${OTHER_CM}`
        })
      })

      await step(`create meeting as eval CM (expected: ${fmtExpected(expectMeeting)})`, async () =>
        probe(
          expectMeeting,
          () =>
            setDoc(meetingRef, {
              studentId: studentRef.id,
              date: '2026-09-15',
              type: 'IEP',
              __smokeTest: true,
              __smokeRunId: ctx.runId
            }),
          { onUnexpectedSuccess: () => deleteDoc(meetingRef).catch(() => {}) }
        )
      )

      if (expectMeeting) {
        await step('delete the smoke meeting', async () => {
          const { allowed } = await attempt(() => deleteDoc(meetingRef))
          return accessDetail(true, allowed)
        })
      }

      await step('delete eval (expected: allowed — create-role)', async () => {
        const { allowed } = await attempt(() => deleteDoc(evalRef))
        evalCreated = !allowed ? evalCreated : false
        return accessDetail(true, allowed)
      })

      await step(
        'leftover evalCaseManagerId after delete is CF lag (inferHiddenOpenEval stays false)',
        async () => {
          let leftover
          try {
            const snap = await getDoc(studentRef)
            leftover = snap.exists() ? snap.data()?.app?.evalCaseManagerId || '' : ''
          } catch (err) {
            if (err?.code !== 'permission-denied') throw err
            return 'read denied — uid already out of staffIds (CF was fast)'
          }
          const hidden = inferHiddenOpenEval({
            visibleOpenEval: null,
            mirrorEvalCaseManagerId: leftover,
            currentUid: uid
          })
          if (hidden) {
            throw new Error(
              'leftover own-uid mirror must not look like a hidden open eval (inferHiddenOpenEval)'
            )
          }
          return leftover === uid
            ? 'mirror still set; inferHiddenOpenEval is false (CF lag)'
            : leftover === ''
              ? 'mirror already cleared; inferHiddenOpenEval is false'
              : `mirror=${leftover}; inferHiddenOpenEval is false`
        }
      )

      await step('Cloud Function clears the mirror', async () => {
        return pollStudent(studentRef, ({ denied, data }) => {
          // For plain CM roles the read flipping BACK to denied is the proof:
          // the uid left staffIds. Full-read roles see the fields directly.
          if (denied) return 'read denied again — uid removed from staffIds'
          if (!data) return 'student already gone'
          const mirror = data.app?.evalCaseManagerId || ''
          const staffIds = data.app?.staffIds || []
          if (mirror === '' && !staffIds.includes(uid)) {
            return 'evalCaseManagerId removed and uid out of staffIds'
          }
          return null
        })
      })

      await step('evalAccess grant is gone too', async () => {
        try {
          const snap = await getDoc(doc(db, 'evalAccess', studentRef.id))
          if (snap.exists()) {
            throw new Error('evalAccess grant still present after the eval was deleted')
          }
          return 'grant deleted'
        } catch (err) {
          // Rules may deny the read of a missing doc depending on role shape —
          // either way the grant is not readable, which is what matters.
          if (err?.code === 'permission-denied') return 'grant unreadable (deleted)'
          throw err
        }
      })
    } finally {
      // Best-effort cleanup; roles that cannot delete students leave a tagged
      // doc for the panel's "Clean up leftovers" sweep.
      await deleteDoc(meetingRef).catch(() => {})
      if (evalCreated) await deleteDoc(evalRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
